import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

/** ---------------- Date label helpers ---------------- **/

// Normalize "YYYY-MM" -> "MM/YYYY", "YYYY-MM-DD" -> "DD/MM/YYYY", else return as-is.
function normalizeDateLabel(raw) {
  if (!raw) return '';
  const s = String(raw).trim();

  const ym = /^(\d{4})-(\d{2})$/;           // 2024-12
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/;  // 2024-12-01

  if (ym.test(s)) {
    const [, y, m] = s.match(ym);
    return `${m}/${y}`;
  }
  if (ymd.test(s)) {
    const [, y, m, d] = s.match(ymd);
    return `${d}/${m}/${y}`;
  }
  return s; // already formatted or custom
}

// Build a human-readable range text with strong single-month detection.
function buildRangeText(analysisType, filters) {
  const r = filters?.range || {};
  const title = (analysisType || '').toLowerCase();

  // 0) UI may pre-format a label
  if (r.label) return String(r.label);

  // 1) Explicit single flags
  if ((r.mode && String(r.mode).toLowerCase() === 'single') || r.single) {
    const val = r.value || r.from || r.to || filters?.month;
    if (val) return normalizeDateLabel(val);
  }

  // 2) If analysis title suggests a single-month view ("... OF THE MONTH")
  if (title.includes('of the month')) {
    // Prefer a month-specific field if present
    if (r.value) return normalizeDateLabel(r.value);
    if (filters?.month) return normalizeDateLabel(filters.month);
    if (r.from) return normalizeDateLabel(r.from);
  }

  // 3) If both from & to are present…
  if (r.from && r.to) {
    const fromLabel = normalizeDateLabel(r.from);
    const toLabel = normalizeDateLabel(r.to);
    // If normalized labels equal, treat as single (e.g., same month)
    if (fromLabel === toLabel) return fromLabel;
    return `${fromLabel} to ${toLabel}`;
  }

  // 4) Single month via filters.month
  if (filters?.month) return normalizeDateLabel(filters.month);

  // 5) Only 'from'
  if (r.from) return normalizeDateLabel(r.from);

  return '';
}

/** ---------------- Drawing helpers ---------------- **/

// Draw "Label: Value" with wrapping; return new Y position.
function drawField(pdf, { label, value, x, y, maxWidth, lineGap = 5 }) {
  if (!value) return y;

  const lbl = `${label}:`;
  const labelWidth = pdf.getTextWidth(lbl);
  const valueMax = Math.max(10, maxWidth - labelWidth - 4);

  pdf.setFont(undefined, 'bold');
  pdf.text(lbl, x, y);

  pdf.setFont(undefined, 'normal');
  const wrapped = pdf.splitTextToSize(String(value), valueMax);
  pdf.text(wrapped, x + labelWidth + 4, y);

  const lines = Array.isArray(wrapped) ? wrapped.length : 1;
  return y + lines * lineGap;
}

// Draw header section (title, filters, timestamp) - reusable for both pages
function drawHeader(pdf, analysisType, filters, pageWidth, margin) {
  const gutter = 12;
  const colWidth = (pageWidth - margin * 2 - gutter) / 2;

  const leftX = margin;
  const rightX = margin + colWidth + gutter;
  let yLeft = 30;
  let yRight = 30;

  /** Title **/
  pdf.setFontSize(16);
  pdf.setFont(undefined, 'bold');
  pdf.text((analysisType || '').toUpperCase(), pageWidth / 2, 15, { align: 'center' });

  /** Two-column filter block **/
  pdf.setFontSize(10);
  pdf.setFont(undefined, 'normal');

  // Left column - Global, Region, Client, Address
  if (filters?.global)
    yLeft = drawField(pdf, { label: 'Global', value: filters.global, x: leftX, y: yLeft, maxWidth: colWidth });
  if (filters?.region)
    yLeft = drawField(pdf, { label: 'Region', value: filters.region, x: leftX, y: yLeft, maxWidth: colWidth });
  if (filters?.client)
    yLeft = drawField(pdf, { label: 'Client', value: filters.client, x: leftX, y: yLeft, maxWidth: colWidth });
  if (filters?.address)
    yLeft = drawField(pdf, { label: 'Address', value: filters.address, x: leftX, y: yLeft, maxWidth: colWidth });

  // Right column - Location, Device, Species, Threshold, Range
  if (filters?.location)
    yRight = drawField(pdf, { label: 'Location', value: filters.location, x: rightX, y: yRight, maxWidth: colWidth });
  if (filters?.device)
    yRight = drawField(pdf, { label: 'Device', value: filters.device, x: rightX, y: yRight, maxWidth: colWidth });
  if (filters?.species)
    yRight = drawField(pdf, { label: 'Species', value: filters.species, x: rightX, y: yRight, maxWidth: colWidth });
  if (filters?.threshold)
    yRight = drawField(pdf, { label: 'Threshold', value: filters.threshold, x: rightX, y: yRight, maxWidth: colWidth });

  // Range (handles single month properly now)
  const rangeText = buildRangeText(analysisType, filters);
  if (rangeText) {
    yRight = drawField(pdf, { label: 'Range', value: rangeText, x: rightX, y: yRight, maxWidth: colWidth });
  }

  // Position directly below both columns
  let yCurrent = Math.max(yLeft, yRight) + 5;

  // Timestamp below the two columns (right-aligned)
  const timestamp = new Date().toLocaleString('en-SG', {
    timeZone: 'Asia/Singapore',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false
  });

  pdf.text(`Generated at: ${timestamp}`, pageWidth - margin, yCurrent, { align: 'right' });
  yCurrent += 8;

  return yCurrent;
}

export async function exportAnalyticsToPDF(analysisType, filters) {
  try {
    const pdf = new jsPDF('l', 'mm', 'a4'); // landscape
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 15;

    /** PAGE 1 - Chart **/
    let yCurrent = drawHeader(pdf, analysisType, filters, pageWidth, margin);

    const chartElement = document.querySelector('.acnt__panel--chart .acnt__panel-body');
    if (chartElement) {
      const chartCanvas = await html2canvas(chartElement, {
        scale: 2,
        logging: false,
        useCORS: true,
        backgroundColor: '#ffffff'
      });

      const img = chartCanvas.toDataURL('image/png');
      const imgW = pageWidth - margin * 2;
      const imgH = (chartCanvas.height * imgW) / chartCanvas.width;

      // Calculate available height and scale down if needed
      const availableHeight = pageHeight - yCurrent - margin;
      let finalWidth = imgW;
      let finalHeight = imgH;

      if (imgH > availableHeight) {
        const scale = availableHeight / imgH;
        finalHeight = availableHeight;
        finalWidth = imgW * scale;
      }

      // Center the chart horizontally if scaled down
      const chartX = margin + (imgW - finalWidth) / 2;
      pdf.addImage(img, 'PNG', chartX, yCurrent, finalWidth, finalHeight);
    }

    /** PAGE 2 - Table **/
    const tableElement = document.querySelector('.acnt__panel--table .acnt__panel-body');
    if (tableElement) {
      pdf.addPage();
      yCurrent = drawHeader(pdf, analysisType, filters, pageWidth, margin);

      const tableCanvas = await html2canvas(tableElement, {
        scale: 2,
        logging: false,
        useCORS: true,
        backgroundColor: '#ffffff'
      });

      const tableImg = tableCanvas.toDataURL('image/png');
      const tWidth = pageWidth - margin * 2;
      const tHeight = (tableCanvas.height * tWidth) / tableCanvas.width;

      // Calculate available height and scale down if needed
      const availableHeight = pageHeight - yCurrent - margin;
      let finalWidth = tWidth;
      let finalHeight = tHeight;

      if (tHeight > availableHeight) {
        const scale = availableHeight / tHeight;
        finalHeight = availableHeight;
        finalWidth = tWidth * scale;
      }

      // Center the table horizontally if scaled down
      const tableX = margin + (tWidth - finalWidth) / 2;
      pdf.addImage(tableImg, 'PNG', tableX, yCurrent, finalWidth, finalHeight);
    }

    /** Save **/
    const safeType = (analysisType || '').replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
    const fname = `${safeType}_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.pdf`;
    pdf.save(fname);

    return { success: true };
  } catch (error) {
    console.error('Error generating PDF:', error);
    return { success: false, error: error.message };
  }
}